import { useState } from 'react';
import api from '../api/endpoints';
import { Chip, Input } from './Ui';
import { useAuth } from '../context/AuthContext';
import { getCurrentPosition, requestLocationPermission } from '../services/location';

/**
 * The parts every address form shares: a "use my current location" button
 * that fills the fields (all still editable), and "who is this for?" - me,
 * or someone else whose name and phone the shop then calls.
 */
const AddressFields = ({ value, onChange, fields = ['line1', 'landmark', 'city', 'pincode'], onError }) => {
  const { user } = useAuth();
  const [locating, setLocating] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });

  const useCurrent = async () => {
    setLocating(true);
    try {
      const allowed = await requestLocationPermission();
      if (!allowed) { onError && onError('Allow location access to use your current position'); return; }
      const fix = await getCurrentPosition({ fresh: true });
      if (!fix || !fix.coordinates) { onError && onError('Could not get your position'); return; }
      const [longitude, latitude] = fix.coordinates;
      let a = {};
      try { a = (await api.maps.reverse({ lat: latitude, lng: longitude })).data || {}; } catch (e) { /* keep the point */ }
      set({
        latitude, longitude,
        line1: a.address || value.line1, city: a.city || value.city, district: a.district || value.district,
        state: a.state || value.state, pincode: a.pincode || value.pincode,
      });
    } catch (e) { onError && onError('Could not get your position'); } finally { setLocating(false); }
  };

  const setWho = (forOther) => set({
    forOther,
    name: forOther ? (value.forOther ? value.name : '') : ((user && user.name) || ''),
    phone: forOther ? (value.forOther ? value.phone : '') : ((user && user.phone) || ''),
  });

  return (
    <div>
      <button type="button" className="card press" onClick={useCurrent} disabled={locating}
        style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 12, alignItems: 'center', background: 'var(--primary-light)', borderColor: 'var(--primary-soft)', marginBottom: 14 }}>
        <span style={{ fontSize: 18 }}>📍</span>
        <span>
          <div className="t-title" style={{ color: 'var(--primary)' }}>{locating ? 'Finding you…' : 'Use my current location'}</div>
          <div className="t-tiny">Fills in the address below - you can edit anything.</div>
        </span>
      </button>

      <label className="field-label">Who is this for?</label>
      <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
        <Chip label="🙋 Me" active={!value.forOther} onClick={() => setWho(false)} />
        <Chip label="👥 Someone else" active={Boolean(value.forOther)} onClick={() => setWho(true)} />
      </div>
      {value.forOther ? (
        <>
          <div className="t-tiny" style={{ marginBottom: 10 }}>Their name and number go on the booking, and the shop or provider will call them.</div>
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Their name" icon="👤" value={value.name || ''} onChange={(e) => set({ name: e.target.value })} />
            <Input label="Their phone number" icon="📞" inputMode="numeric" maxLength={10} value={value.phone || ''} onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '') })} />
          </div>
        </>
      ) : null}

      {fields.includes('line1') ? <Input label="Address" placeholder="House / flat / street" value={value.line1 || ''} onChange={(e) => set({ line1: e.target.value })} /> : null}
      <div className="split-2" style={{ gap: 12 }}>
        {fields.includes('landmark') ? <Input label="Landmark" placeholder="Near…" value={value.landmark || ''} onChange={(e) => set({ landmark: e.target.value })} /> : null}
        {fields.includes('city') ? <Input label="City" value={value.city || ''} onChange={(e) => set({ city: e.target.value })} /> : null}
      </div>
      {fields.includes('pincode') ? <Input label="Pincode" inputMode="numeric" maxLength={6} value={value.pincode || ''} onChange={(e) => set({ pincode: e.target.value.replace(/\D/g, '') })} /> : null}
    </div>
  );
};

export default AddressFields;
